import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertTriangle, Menu, X, CalendarIcon, FileText, Table2,
  Gauge, Moon, Sun, EyeOff, CheckCircle, XCircle, ArrowRight, Lock
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { OwnerSidebar } from '@/components/OwnerSidebar';
import { HelpButton } from '@/components/HelpButton';
import { FirstStepsChecklist } from '@/components/FirstStepsChecklist';
import { CompanySelector } from '@/components/CompanySelector';
import { LocationPrompt } from '@/components/LocationPrompt';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import { DashboardKPIs } from '@/pages/dashboard/DashboardKPIs';
import { DashboardComparison } from '@/pages/dashboard/DashboardComparison';
import { DashboardCharts } from '@/pages/dashboard/DashboardCharts';
import { DashboardReferral } from '@/pages/dashboard/DashboardReferral';
import { DashboardFeedbackFeed } from '@/pages/dashboard/DashboardFeedbackFeed';
import { DashboardModals } from '@/pages/dashboard/DashboardModals';
import { KPIHistoryModal } from '@/pages/dashboard/KPIHistoryModal';
import { KPI_METRICS } from '@/pages/dashboard/kpiMetrics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [period, setPeriod] = useState('30days');
  const [feedbackType, setFeedbackType] = useState('all');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [planUsage, setPlanUsage] = useState(null);
  const [showWhatsAppLock, setShowWhatsAppLock] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('dashboard_dark_mode') === 'true');
  const [referralData, setReferralData] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [showArchiveLock, setShowArchiveLock] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [monthlyEvolution, setMonthlyEvolution] = useState([]);
  const [loadingEvolution, setLoadingEvolution] = useState(false);
  const [historyMetric, setHistoryMetric] = useState(null);
  const [complaintTagsStats, setComplaintTagsStats] = useState(null);
  const [docValue, setDocValue] = useState('');
  const [docSaving, setDocSaving] = useState(false);
  const [retention, setRetention] = useState(null);

  const submitPendingDoc = async () => {
    const pd = planUsage?.pending_document;
    const doc = docValue.replace(/\D/g, '');
    if (doc.length !== 11 && doc.length !== 14) { toast.error('Informe um CPF ou CNPJ válido'); return; }
    setDocSaving(true);
    try {
      const r = await axios.post(`${API}/quick-signup/complete`, { signup_token: pd.resume_token, cpf_cnpj: doc });
      toast.success(`Boleto gerado! Vence em ${r.data.due_date}`);
      setDocValue('');
      checkOnboarding();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar o boleto'); }
    finally { setDocSaving(false); }
  };

  const canArchive = planUsage && ['enterprise', 'elite', 'five_stars'].includes(planUsage.plan);
  const dm = darkMode;
  const theme = {
    dm,
    bg: dm ? 'bg-[#0B1120]' : 'bg-[#F8FAFC]',
    cardBg: dm ? 'bg-[#1A2332] border-slate-700/50' : 'bg-white border-slate-200',
    headerBg: dm ? 'bg-[#111827] border-slate-700/50' : 'bg-white border-slate-200',
    textPrimary: dm ? 'text-slate-100' : 'text-slate-900',
    textSecondary: dm ? 'text-slate-400' : 'text-slate-500',
    textMuted: dm ? 'text-slate-500' : 'text-slate-400',
    borderColor: dm ? 'border-slate-700/50' : 'border-slate-100',
  };

  const toggleDarkMode = () => {
    setDarkMode(prev => { localStorage.setItem('dashboard_dark_mode', String(!prev)); return !prev; });
  };

  const archiveFeedback = useCallback(async (feedbackId) => {
    if (!canArchive) { setShowArchiveLock(true); return; }
    try {
      const token = getToken();
      await axios.put(`${API}/feedbacks/${feedbackId}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(showArchived ? 'Avaliação restaurada!' : 'Avaliação ocultada com sucesso');
      fetchData();
    } catch (err) {
      if (err.response?.status === 403) setShowArchiveLock(true);
      else toast.error('Erro ao processar avaliação');
    }
  }, [canArchive, getToken, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authLoading && !user && !localStorage.getItem('token')) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) { fetchData(); checkOnboarding(); fetchReferralData(); }
  }, [user, period, feedbackType, dateRange, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReferralData = useCallback(async () => {
    setLoadingReferral(true);
    try {
      const res = await axios.get(`${API}/company/referral`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setReferralData(res.data);
    } catch { /* silent */ } finally { setLoadingReferral(false); }
  }, [getToken]);

  const checkOnboarding = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const usageRes = await axios.get(`${API}/company/plan-usage`, { headers });
      setPlanUsage(usageRes.data);
    } catch { /* silent */ }
  }, [getToken]);

  const fetchData = useCallback(async () => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    let params = { period };
    if (period === 'custom' && dateRange.from && dateRange.to) {
      params.start_date = dateRange.from.toISOString();
      params.end_date = dateRange.to.toISOString();
    }
    try {
      const feedbackParams = { ...params, feedback_type: feedbackType };
      if (showArchived) feedbackParams.show_archived = 'only';
      const [statsRes, feedbacksRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers, params }),
        axios.get(`${API}/feedbacks`, { headers, params: feedbackParams })
      ]);
      setStats(statsRes.data);
      const fbData = feedbacksRes.data;
      setFeedbacks(fbData.feedbacks || fbData);
      setHiddenCount(fbData.retention?.retained ? 0 : (fbData.hidden_count || 0));
      setRetention(fbData.retention || null);
      try {
        const tagsRes = await axios.get(`${API}/feedbacks/complaint-tags-stats`, { headers, params: { period } });
        setComplaintTagsStats(tagsRes.data);
      } catch { /* tags not critical */ }
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, [period, feedbackType, dateRange, showArchived, getToken]);

  const loadMonthlyEvolution = useCallback(async () => {
    setLoadingEvolution(true);
    try {
      const res = await axios.get(`${API}/feedbacks/monthly-evolution?months=6`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMonthlyEvolution(res.data.months || []);
    } catch { /* silent — não bloqueia o dashboard */ }
    finally { setLoadingEvolution(false); }
  }, [getToken]);

  const openHistory = useCallback((metricKey) => {
    setHistoryMetric(KPI_METRICS[metricKey] || null);
  }, []);

  const openWhatsApp = useCallback((feedback) => {
    const message = encodeURIComponent(`Ola ${feedback.customer_name}! Recebemos seu feedback e agradecemos por compartilhar sua experiência. Gostariamos de conversar mais.`);
    const phone = feedback.customer_whatsapp.replace(/\D/g, '');
    window.open(`https://wa.me/55${phone}?text=${message}`, '_blank');
    axios.put(`${API}/feedbacks/${feedback.id}/respond`, {}, { headers: { Authorization: `Bearer ${getToken()}` } }).then(() => fetchData()).catch(() => {});
  }, [getToken, fetchData]);

  const openEmail = useCallback((feedback) => {
    window.location.href = `mailto:${feedback.customer_email}?subject=Resposta ao seu feedback&body=Ola ${feedback.customer_name}, agradecemos seu feedback!`;
    axios.put(`${API}/feedbacks/${feedback.id}/respond`, {}, { headers: { Authorization: `Bearer ${getToken()}` } }).then(() => fetchData()).catch(() => {});
  }, [getToken, fetchData]);

  // Onboarding tour
  useEffect(() => {
    if (loading || authLoading || localStorage.getItem('onboarding_v1') === 'seen') return;
    const timer = setTimeout(() => {
      const driverObj = driver({
        showProgress: true, animate: true, overlayColor: 'rgba(0,0,0,0.75)',
        stagePadding: 8, stageRadius: 12, popoverClass: 'onboarding-popover',
        prevBtnText: 'Anterior', nextBtnText: 'Próximo', doneBtnText: 'Começar Agora!',
        onDestroyed: () => localStorage.setItem('onboarding_v1', 'seen'),
        steps: [
          { element: '#sidebar-settings', popover: { title: 'Sua Arma de Vendas', description: 'Baixe sua etiqueta profissional aqui.', side: 'right', align: 'center' } },
          { element: '[data-testid="kpi-rating"]', popover: { title: 'O Pulso do Seu Negócio', description: 'Acompanhe sua nota real em tempo real.', side: 'bottom', align: 'center' } },
          { element: '[data-testid="feedback-feed"]', popover: { title: 'Resposta Imediata', description: 'Se um cliente der nota baixa, fale com ele na hora pelo WhatsApp.', side: 'top', align: 'center' } }
        ]
      });
      driverObj.drive();
    }, 1200);
    return () => clearTimeout(timer);
  }, [loading, authLoading]);

  // Export PDF
  const exportPDF = useCallback(() => {
    if (feedbacks.length === 0) { toast.error('Nenhuma avaliação para exportar'); return; }
    try {
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const navy = [10, 38, 71]; const gold = [197, 160, 89];
      pdf.setFillColor(...navy); pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), 28, 'F');
      pdf.setFontSize(16); pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold');
      pdf.text('FALO COM O DONO - Relatorio de Avaliações', 14, 12);
      pdf.setFontSize(9); pdf.setTextColor(...gold);
      pdf.text(`${user?.company_name || ''} | Gerado em ${new Date().toLocaleDateString('pt-BR')} | Total: ${feedbacks.length} avaliações`, 14, 20);
      if (stats) { pdf.setFontSize(8); pdf.setTextColor(100,100,100); pdf.text(`Media: ${stats.average_rating}/5 | Satisfação: ${stats.satisfaction_percentage}% | Reclamacoes: ${stats.complaints_count} | Sugestões: ${stats.suggestions_count} | Elogios: ${stats.praises_count} | Urgentes: ${stats.urgent_count}`, 14, 26); }
      const fmtDateTime = (iso) => new Date(iso).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' });
      const shiftOf = (iso) => { const h = new Date(iso).getHours(); if (h >= 6 && h < 12) return 'Manha'; if (h >= 12 && h < 18) return 'Tarde'; if (h >= 18 && h < 23) return 'Noite'; return 'Madrugada'; };
      const tableData = feedbacks.map(f => [fmtDateTime(f.created_at), shiftOf(f.created_at), f.customer_name, f.feedback_type === 'complaint' ? 'Reclamação' : f.feedback_type === 'suggestion' ? 'Sugestão' : 'Elogio', `${f.rating_food}`, `${f.rating_service}`, `${f.rating_ambiance}`, `${f.rating_average}`, f.message.length > 80 ? f.message.substring(0, 77) + '...' : f.message, f.is_urgent ? 'SIM' : '', f.responded ? 'Sim' : 'Não']);
      autoTable(pdf, { startY: 32, head: [['Data/Hora', 'Turno', 'Cliente', 'Tipo', 'Produtos', 'Atend.', 'Amb.', 'Media', 'Mensagem', 'Urgente', 'Resp.']], body: tableData, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: navy, textColor: [255,255,255], fontStyle: 'bold', fontSize: 7.5 }, alternateRowStyles: { fillColor: [248,250,252] }, columnStyles: { 0:{cellWidth:22}, 1:{cellWidth:16,halign:'center'}, 2:{cellWidth:24}, 3:{cellWidth:20}, 4:{cellWidth:12,halign:'center'}, 5:{cellWidth:12,halign:'center'}, 6:{cellWidth:12,halign:'center'}, 7:{cellWidth:12,halign:'center'}, 8:{cellWidth:'auto'}, 9:{cellWidth:14,halign:'center'}, 10:{cellWidth:12,halign:'center'} }, didParseCell(data) { if (data.section==='body') { if (data.column.index===3) { const t=data.cell.raw; if (t==='Reclamação') data.cell.styles.textColor=[220,38,38]; else if (t==='Sugestão') data.cell.styles.textColor=[37,99,235]; else data.cell.styles.textColor=[217,119,6]; } if (data.column.index===9&&data.cell.raw==='SIM') { data.cell.styles.textColor=[220,38,38]; data.cell.styles.fontStyle='bold'; } } } });
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i=1;i<=pageCount;i++) { pdf.setPage(i); pdf.setFontSize(7); pdf.setTextColor(150,150,150); pdf.text(`FaleComODono.com.br | Pagina ${i} de ${pageCount}`, 14, pdf.internal.pageSize.getHeight()-6); }
      pdf.save(`relatorio-avaliações-${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success('Relatorio PDF gerado!');
    } catch { toast.error('Erro ao gerar PDF'); }
  }, [feedbacks, stats, user]);

  // Export Excel
  const exportExcel = useCallback(() => {
    if (feedbacks.length === 0) { toast.error('Nenhuma avaliação para exportar'); return; }
    try {
      const fmtDateTimeXls = (iso) => new Date(iso).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
      const shiftOfXls = (iso) => { const h = new Date(iso).getHours(); if (h >= 6 && h < 12) return 'Manha'; if (h >= 12 && h < 18) return 'Tarde'; if (h >= 18 && h < 23) return 'Noite'; return 'Madrugada'; };
      const data = feedbacks.map(f => ({ 'Data/Hora': fmtDateTimeXls(f.created_at), 'Turno': shiftOfXls(f.created_at), 'Cliente': f.customer_name, 'WhatsApp': f.customer_whatsapp, 'E-mail': f.customer_email, 'Tipo': f.feedback_type === 'complaint' ? 'Reclamação' : f.feedback_type === 'suggestion' ? 'Sugestão' : 'Elogio', 'Produtos': f.rating_food, 'Atendimento': f.rating_service, 'Ambiente': f.rating_ambiance, 'Media': f.rating_average, 'Mensagem': f.message, 'Urgente': f.is_urgent ? 'Sim' : 'Não', 'Respondido': f.responded ? 'Sim' : 'Não' }));
      const summaryData = [{ 'Indicador': 'Total de Avaliações', 'Valor': stats?.total_feedbacks||0 }, { 'Indicador': 'Media Geral', 'Valor': stats?.average_rating||0 }, { 'Indicador': 'Satisfação (%)', 'Valor': stats?.satisfaction_percentage||0 }, { 'Indicador': 'Convidados ao Google (%)', 'Valor': stats?.public_recommendation_rate||0 }, { 'Indicador': 'Reclamacoes', 'Valor': stats?.complaints_count||0 }, { 'Indicador': 'Sugestões', 'Valor': stats?.suggestions_count||0 }, { 'Indicador': 'Elogios', 'Valor': stats?.praises_count||0 }, { 'Indicador': 'Urgentes', 'Valor': stats?.urgent_count||0 }, { 'Indicador': 'Media Produtos', 'Valor': stats?.ratings_by_category?.food||0 }, { 'Indicador': 'Media Atendimento', 'Valor': stats?.ratings_by_category?.service||0 }, { 'Indicador': 'Media Ambiente', 'Valor': stats?.ratings_by_category?.ambiance||0 }];
      // Distribuicao por turno (insight extra para o user analisar)
      const turnoBuckets = { Manha: 0, Tarde: 0, Noite: 0, Madrugada: 0 };
      feedbacks.forEach(f => { turnoBuckets[shiftOfXls(f.created_at)] += 1; });
      const turnoData = Object.entries(turnoBuckets).map(([turno, total]) => ({ 'Turno': turno, 'Total de Avaliacoes': total, '% do total': feedbacks.length ? Math.round((total / feedbacks.length) * 100) + '%' : '0%' }));
      const wb = XLSX.utils.book_new();
      const wsFeedbacks = XLSX.utils.json_to_sheet(data); wsFeedbacks['!cols'] = [{wch:16},{wch:10},{wch:25},{wch:18},{wch:25},{wch:14},{wch:8},{wch:12},{wch:10},{wch:8},{wch:50},{wch:10},{wch:12}];
      XLSX.utils.book_append_sheet(wb, wsFeedbacks, 'Avaliações');
      const wsSummary = XLSX.utils.json_to_sheet(summaryData); wsSummary['!cols'] = [{wch:22},{wch:15}];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo');
      const wsTurno = XLSX.utils.json_to_sheet(turnoData); wsTurno['!cols'] = [{wch:14},{wch:22},{wch:14}];
      XLSX.utils.book_append_sheet(wb, wsTurno, 'Por Turno');
      XLSX.writeFile(wb, `relatorio-avaliações-${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success('Relatorio Excel gerado!');
    } catch { toast.error('Erro ao gerar Excel'); }
  }, [feedbacks, stats]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const getFeedbackTypeName = (type) => type === 'complaint' ? 'Reclamação' : type === 'suggestion' ? 'Sugestão' : type === 'praise' ? 'Elogio' : type;

  const referralLink = referralData?.referral_code ? `${window.location.origin}/indicacao/${referralData.referral_code}` : '';
  const copyReferralLink = () => { navigator.clipboard.writeText(referralLink); toast.success('Link de indicação copiado!'); };
  const shareReferralWhatsApp = () => { window.open(`https://wa.me/?text=${encodeURIComponent(`Conheca o FaloComODono! Use meu link e ganhe acesso ao melhor sistema de avaliações: ${referralLink}`)}`, '_blank'); };
  const onCompanySwitch = () => { checkOnboarding(); fetchReferralData(); fetchData(); };

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><div className="spinner" /></div>;
  if (!user) return null;

  const chartData = stats ? [
    { name: 'Produtos', value: stats.ratings_by_category.food },
    { name: 'Atendimento', value: stats.ratings_by_category.service },
    { name: 'Ambiente', value: stats.ratings_by_category.ambiance }
  ] : [];
  const chartColors = ['#C5A059', '#0A2647', '#3B82F6'];

  return (
    <div className={`min-h-screen ${theme.bg} transition-colors duration-300`} data-testid="dashboard-page">

      {/* Mobile Header */}
      <div className="lg:hidden bg-[#0A2647] text-white h-16 flex items-center justify-between px-4 sticky top-0 z-40">
        <div className="flex items-center gap-3"><span className="font-semibold text-sm">Falo Com O Dono</span></div>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white hover:bg-white/10">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 bg-[#0A2647]">
            <OwnerSidebar user={user} mobile setSidebarOpen={setSidebarOpen} handleLogout={handleLogout} activePage="dashboard" companySelector={<CompanySelector onCompanySwitch={onCompanySwitch} />} />
          </div>
        </div>
      )}

      <div className="hidden lg:block w-64 bg-[#0A2647] fixed left-0 top-0 h-screen z-30">
        <OwnerSidebar user={user} handleLogout={handleLogout} activePage="dashboard" companySelector={<CompanySelector onCompanySwitch={onCompanySwitch} />} />
      </div>

      <div className="lg:ml-64 min-h-screen">
        {/* Header with Filters */}
        <header className={`${theme.headerBg} border-b px-4 sm:px-6 py-3 sm:py-4 sticky top-16 lg:top-0 z-20 transition-colors duration-300`}>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className={`text-base sm:text-xl font-semibold ${theme.textPrimary} truncate`}>Painel do Dono</h1>
                  <div className="bg-[#0A2647] rounded-xl px-1 py-0.5 hidden sm:block"><CompanySelector onCompanySwitch={onCompanySwitch} /></div>
                </div>
                <p className={`text-xs sm:text-sm ${theme.textSecondary} truncate`}>Bem-vindo, {user?.name?.split(' ')[0]}</p>
              </div>
              <button onClick={toggleDarkMode} className={`p-2 rounded-lg transition-all duration-300 shrink-0 ${dm ? 'bg-[#C5A059]/20 text-[#C5A059] hover:bg-[#C5A059]/30' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="dark-mode-toggle" title={dm ? 'Modo Claro' : 'Modo Escuro'}>
                {dm ? <Sun className="w-4 h-4 sm:w-5 sm:h-5" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="sm:hidden bg-[#0A2647] rounded-lg px-1 py-0.5"><CompanySelector onCompanySwitch={onCompanySwitch} /></div>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className={`w-[120px] sm:w-[140px] h-8 sm:h-10 text-xs sm:text-sm ${dm ? 'bg-slate-800 border-slate-700 text-slate-200' : ''}`} data-testid="period-filter"><SelectValue placeholder="Período" /></SelectTrigger>
                <SelectContent className={dm ? 'bg-slate-800 border-slate-700 text-slate-200' : ''}>
                  <SelectItem value="today">Hoje</SelectItem><SelectItem value="yesterday">Ontem</SelectItem>
                  <SelectItem value="7days">7 dias</SelectItem><SelectItem value="15days">15 dias</SelectItem>
                  <SelectItem value="30days">30 dias</SelectItem><SelectItem value="custom">Período</SelectItem>
                </SelectContent>
              </Select>
              {period === 'custom' && (
                <Popover><PopoverTrigger asChild>
                  <Button variant="outline" className={`h-8 sm:h-10 gap-1.5 text-xs sm:text-sm px-2 sm:px-3 ${dm ? 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white' : ''}`}><CalendarIcon className="w-3.5 h-3.5" />{dateRange.from && dateRange.to ? `${format(dateRange.from, 'dd/MM')} - ${format(dateRange.to, 'dd/MM')}` : 'Datas'}</Button>
                </PopoverTrigger><PopoverContent className="w-auto p-0" align="end"><Calendar mode="range" selected={dateRange} onSelect={setDateRange} locale={ptBR} /></PopoverContent></Popover>
              )}
              <Select value={feedbackType} onValueChange={setFeedbackType}>
                <SelectTrigger className={`w-[110px] sm:w-[140px] h-8 sm:h-10 text-xs sm:text-sm ${dm ? 'bg-slate-800 border-slate-700 text-slate-200' : ''}`} data-testid="type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent className={dm ? 'bg-slate-800 border-slate-700 text-slate-200' : ''}>
                  <SelectItem value="all">Todos</SelectItem><SelectItem value="complaint">Reclamacoes</SelectItem>
                  <SelectItem value="suggestion">Sugestões</SelectItem><SelectItem value="praise">Elogios</SelectItem>
                </SelectContent>
              </Select>
              {canArchive && (
                <button onClick={() => setShowArchived(!showArchived)} className={`h-8 sm:h-10 px-2 sm:px-3 rounded-lg text-[10px] sm:text-xs font-medium flex items-center gap-1 border transition-all ${showArchived ? (dm ? 'bg-[#C5A059]/20 border-[#C5A059]/40 text-[#C5A059]' : 'bg-[#0A2647] border-[#0A2647] text-white') : (dm ? 'bg-slate-700 border-slate-600 text-slate-400 hover:text-[#C5A059] hover:border-[#C5A059]/40' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300')}`} data-testid="toggle-archived">
                  <EyeOff className="w-3 h-3 sm:w-3.5 sm:h-3.5" />{showArchived ? 'Ocultas' : 'Ocultas'}
                </button>
              )}
            </div>
          </div>
        </header>

        <main className="p-3 sm:p-6">
          {/* Avaliação Retida: card no topo com a contagem e o botão de liberar */}
          {retention?.retained && (
            <div className="mb-4 rounded-2xl border-2 border-[#C5A059] bg-gradient-to-r from-[#0A2647] to-[#133c6b] p-5 sm:p-6 text-white shadow-lg" data-testid="retention-card">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-[#C5A059]/20 shrink-0">
                    <Lock className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-lg sm:text-xl font-bold" data-testid="retention-count">
                      Você tem {retention.pending_count} {retention.pending_count === 1 ? 'avaliação' : 'avaliações'} esperando por você.
                    </p>
                    <p className="text-sm text-slate-200 mt-0.5">
                      Regularize seu plano para ver tudo na hora.
                    </p>
                  </div>
                </div>
                <a
                  href={retention.invoice_url || '/plans'}
                  target={retention.invoice_url ? '_blank' : undefined}
                  rel="noreferrer"
                  className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#C5A059] text-[#0A2647] font-bold text-sm hover:brightness-110 transition-all"
                  data-testid="retention-release-btn"
                >
                  Liberar minhas avaliações
                </a>
              </div>
            </div>
          )}

          {/* Location Prompt: empresas legadas sem city/state */}
          <LocationPrompt getToken={getToken} dm={dm} />

          {/* Sponsorship Banner — cliente patrocinado por um Parceiro */}
          {planUsage?.sponsorship && (() => {
            const sp = planUsage.sponsorship;
            const phase = sp.status_phase || 'active';
            const days = sp.days_until_expiration;

            // Configuração visual por fase
            const config = {
              active:           { bg: dm ? 'from-[#C5A059]/10 to-[#0A2647]/30 border-[#C5A059]/30' : 'from-amber-50 to-white border-[#C5A059]/30', icon: 'text-[#C5A059]', iconBg: 'bg-[#C5A059]/20' },
              warning_30:       { bg: dm ? 'from-blue-900/30 to-blue-800/10 border-blue-700/40' : 'from-blue-50 to-white border-blue-300', icon: 'text-blue-500', iconBg: 'bg-blue-500/20' },
              warning_15:       { bg: dm ? 'from-amber-900/30 to-amber-800/10 border-amber-700/40' : 'from-amber-50 to-amber-100/30 border-amber-300', icon: 'text-amber-600', iconBg: 'bg-amber-500/20' },
              warning_7:        { bg: dm ? 'from-orange-900/30 to-orange-800/10 border-orange-700/40' : 'from-orange-50 to-orange-100/30 border-orange-300', icon: 'text-orange-600', iconBg: 'bg-orange-500/20' },
              warning_1:        { bg: dm ? 'from-rose-900/30 to-rose-800/10 border-rose-700/40' : 'from-rose-50 to-rose-100/30 border-rose-300', icon: 'text-rose-600', iconBg: 'bg-rose-500/20' },
              expired_unchosen: { bg: dm ? 'from-rose-900/40 to-rose-950/40 border-rose-700/60' : 'from-rose-100 to-rose-50 border-rose-400', icon: 'text-rose-700', iconBg: 'bg-rose-500/30' },
            }[phase] || config?.active;

            const titleByPhase = {
              active:           `Seu Plano 5 Estrelas é cortesia de ${sp.partner_name}`,
              warning_30:       `Seu patrocínio termina em 30 dias — comece a planejar`,
              warning_15:       `Seu patrocínio termina em ${days} dias — escolha um plano em breve`,
              warning_7:        `Atenção: patrocínio termina em ${days} dias`,
              warning_1:        `Último dia! Seu patrocínio termina ${days === 0 ? 'hoje' : 'amanhã'}`,
              expired_unchosen: `Seu patrocínio encerrou — escolha um plano para continuar`,
            };

            const subtitleByPhase = {
              active:           sp.expires_at ? `Válido até ${new Date(sp.expires_at).toLocaleDateString('pt-BR')} — aproveite todos os recursos sem custo.` : 'Aproveite todos os recursos sem custo.',
              warning_30:       sp.expires_at ? `Após ${new Date(sp.expires_at).toLocaleDateString('pt-BR')} sua conta precisa migrar para um plano pago para continuar.` : '',
              warning_15:       sp.expires_at ? `Você tem até ${new Date(sp.expires_at).toLocaleDateString('pt-BR')} para escolher. Depois disso, +7 dias antes do downgrade para Free.` : '',
              warning_7:        `Escolha um plano para evitar o downgrade automático ao Free (15 avaliações/mês).`,
              warning_1:        `Não perca recursos! Escolha um plano agora para garantir continuidade total.`,
              expired_unchosen: `Sua conta segue ativa, mas você precisa escolher um plano em até 7 dias para não cair no Free.`,
            };

            return (
              <div className={`p-4 rounded-2xl border mb-6 flex items-center gap-3 bg-gradient-to-br ${config?.bg}`} data-testid="sponsorship-banner">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${config?.iconBg}`}>
                  {phase === 'expired_unchosen' ? <AlertTriangle className={`w-5 h-5 ${config?.icon}`} /> : <CheckCircle className={`w-5 h-5 ${config?.icon}`} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${dm ? 'text-white' : 'text-[#0A2647]'}`}>{titleByPhase[phase]}</p>
                  <p className={`text-xs mt-0.5 ${dm ? 'text-slate-300' : 'text-slate-600'}`}>{subtitleByPhase[phase]}</p>
                </div>
                {phase !== 'active' && (
                  <Link to="/plans" className="flex-shrink-0">
                    <Button size="sm" className="bg-[#C5A059] hover:bg-[#b08e4a] text-white gap-1.5" data-testid="banner-choose-plan-btn">
                      Escolher plano <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </Link>
                )}
              </div>
            );
          })()}

          {/* Modal bloqueante: patrocinio expirado e cliente nao escolheu plano */}
          {planUsage?.sponsorship?.must_choose_plan && (
            <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" data-testid="sponsorship-expired-modal">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-rose-600" />
                </div>
                <h2 className="text-xl font-bold text-[#0A2647] mb-2">Seu patrocínio encerrou</h2>
                <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                  O Plano 5 Estrelas patrocinado{planUsage.sponsorship.partner_name ? <> por <strong>{planUsage.sponsorship.partner_name}</strong></> : ''} chegou ao fim.
                  Para continuar usando o Falo com o Dono <strong>sem interrupção</strong>, escolha um plano agora.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-xs text-amber-900 text-left">
                  Se nenhum plano for escolhido em <strong>7 dias</strong>, sua conta cairá automaticamente no Plano Free (15 avaliações/mês). Seus dados serão preservados.
                </div>
                <Link to="/plans">
                  <Button className="w-full bg-[#C5A059] hover:bg-[#b08e4a] text-white gap-2 h-12 font-semibold" data-testid="modal-choose-plan-btn">
                    Ver planos disponíveis <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Ativação assistida: plano liberado, falta CPF/CNPJ */}
          {planUsage?.pending_document?.pending && !(planUsage.pending_document.block_enabled && planUsage.pending_document.blocked) && (
            <div className={`p-4 rounded-2xl border mb-6 flex flex-wrap items-center gap-3 bg-gradient-to-br ${dm ? 'from-amber-900/30 to-amber-800/10 border-amber-700/40' : 'from-amber-50 to-white border-amber-300'}`} data-testid="pending-doc-banner">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className={`text-sm font-bold ${dm ? 'text-white' : 'text-[#0A2647]'}`}>
                  Falta 1 passo: informe seu CPF ou CNPJ
                </p>
                <p className={`text-xs mt-0.5 ${dm ? 'text-slate-300' : 'text-slate-600'}`}>
                  Seu plano {planUsage.pending_document.plan_name} (R$ {(planUsage.pending_document.plan_price || 0).toFixed(2).replace('.', ',')}/mês) já está liberado.
                  {planUsage.pending_document.days_left === null ? ''
                    : planUsage.pending_document.days_left > 0
                      ? ` Você tem ${planUsage.pending_document.days_left} dia(s) para regularizar.`
                      : ' O prazo para regularizar já encerrou.'}
                </p>
              </div>
              <div className="flex items-end gap-2">
                <input value={docValue} onChange={e => setDocValue(e.target.value)} placeholder="CPF ou CNPJ" inputMode="numeric"
                  className="h-10 px-3 rounded-lg border border-amber-300 bg-white text-sm text-slate-800 w-[170px]"
                  data-testid="pending-doc-banner-input" />
                <Button size="sm" onClick={submitPendingDoc} disabled={docSaving}
                  className="bg-[#C5A059] hover:bg-[#b08e4a] text-white h-10 gap-1.5" data-testid="pending-doc-banner-btn">
                  Gerar boleto <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Modal bloqueante: prazo do CPF/CNPJ vencido (só com bloqueio habilitado) */}
          {planUsage?.pending_document?.block_enabled === true && planUsage?.pending_document?.blocked === true && (
            <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" data-testid="pending-doc-blocked-modal">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-[#0A2647] mb-2">Informe seu CPF ou CNPJ para continuar</h2>
                <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                  Seu plano <strong>{planUsage.pending_document.plan_name}</strong> foi liberado antecipadamente
                  (R$ {(planUsage.pending_document.plan_price || 0).toFixed(2).replace('.', ',')}/mês) e o prazo para
                  regularizar o cadastro terminou. Informe o documento abaixo para gerar seu boleto e liberar o painel na hora.
                </p>
                <input value={docValue} onChange={e => setDocValue(e.target.value)} placeholder="CPF ou CNPJ" inputMode="numeric"
                  className="w-full h-12 px-4 rounded-lg border border-slate-300 text-sm mb-3"
                  data-testid="pending-doc-blocked-input" />
                <Button onClick={submitPendingDoc} disabled={docSaving}
                  className="w-full bg-[#C5A059] hover:bg-[#b08e4a] text-white gap-2 h-12 font-semibold"
                  data-testid="pending-doc-blocked-btn">
                  {docSaving ? 'Gerando boleto...' : <>Gerar meu boleto e continuar <ArrowRight className="w-4 h-4" /></>}
                </Button>
                <p className="text-[11px] text-slate-500 mt-3">Sem fidelidade. Na página do boleto você também paga com PIX ou cartão.</p>
              </div>
            </div>
          )}

          {/* Setup Checklist */}
          {planUsage?.setup_checklist && !planUsage.setup_checklist.complete && (() => {
            const items = [
              { key: 'logo', label: 'Logo da Empresa', icon: '🎨', done: planUsage.setup_checklist.logo },
              { key: 'google_maps', label: 'Link do Google Maps', icon: '📍', done: planUsage.setup_checklist.google_maps },
              { key: 'contact_email', label: 'E-mail de alerta', icon: '📧', done: planUsage.setup_checklist.contact_email },
              { key: 'contact_whatsapp', label: 'WhatsApp de alerta', icon: '💬', done: planUsage.setup_checklist.contact_whatsapp },
            ];
            const completed = items.filter(i => i.done).length;
            const total = items.length;
            const pct = Math.round((completed / total) * 100);
            const isFirstTime = completed === 0;
            return (
              <div className={`p-5 rounded-2xl border mb-6 ${dm ? 'bg-gradient-to-br from-[#C5A059]/10 to-[#0A2647]/30 border-[#C5A059]/30' : 'bg-gradient-to-br from-amber-50 to-white border-amber-200'}`} data-testid="setup-checklist-widget">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <p className={`text-base font-bold ${dm ? 'text-white' : 'text-[#0A2647]'}`}>
                      {isFirstTime ? `Bem-vindo, ${user?.name?.split(' ')[0]}! 🎉` : 'Quase lá!'}
                    </p>
                    <p className={`text-sm mt-0.5 ${dm ? 'text-slate-300' : 'text-slate-600'}`}>
                      {isFirstTime
                        ? 'Sua máquina de avaliações está 90% pronta. Faltam só 4 ajustes rápidos para começar a receber feedbacks.'
                        : `${completed} de ${total} configurações prontas. Conclua as restantes para ativar tudo.`}
                    </p>
                  </div>
                  <div className={`text-right shrink-0 ${dm ? 'text-[#C5A059]' : 'text-amber-600'}`}>
                    <p className="text-2xl font-extrabold leading-none">{pct}%</p>
                    <p className="text-[10px] uppercase tracking-wider font-semibold mt-0.5">pronto</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div className={`h-2 rounded-full overflow-hidden mb-4 ${dm ? 'bg-white/10' : 'bg-amber-100'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-[#C5A059] to-[#d4af61] rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {items.map(item => (
                    <Link
                      key={item.key}
                      to="/settings?tab=brand"
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all ${
                        item.done
                          ? (dm ? 'bg-emerald-900/20 text-emerald-400 border border-emerald-800/40' : 'bg-emerald-50 text-emerald-700 border border-emerald-200')
                          : (dm ? 'bg-white/5 text-white border border-white/10 hover:bg-white/10' : 'bg-white text-[#0A2647] border border-slate-200 hover:border-[#C5A059]/40 hover:shadow-sm')
                      }`}
                      data-testid={`checklist-${item.key}`}
                    >
                      <span className="text-base shrink-0">{item.done ? '✅' : item.icon}</span>
                      <span className="font-semibold truncate">{item.label}</span>
                      {!item.done && <ArrowRight className="w-3.5 h-3.5 ml-auto shrink-0 opacity-60" />}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Free Trial Alert */}
          {planUsage?.plan === 'free' && planUsage.free_trial_days_remaining !== null && (
            planUsage.free_trial_expired ? (
              <div className={`rounded-xl p-5 mb-6 border-2 ${dm ? 'bg-red-950/40 border-red-700/60' : 'bg-red-50 border-red-300'}`} data-testid="free-trial-expired-alert">
                <div className="flex items-start gap-2 mb-4">
                  <AlertTriangle className={`w-6 h-6 mt-0.5 ${dm ? 'text-red-400' : 'text-red-600'} flex-shrink-0`} />
                  <div>
                    <p className={`font-bold text-lg ${dm ? 'text-red-300' : 'text-red-800'}`}>Seu período gratuito encerrou</p>
                    <p className={`text-sm mt-0.5 ${dm ? 'text-red-300/80' : 'text-red-700'}`}>
                      Suas avaliações estão bloqueadas para visualização. Escolha o plano que melhor atende seu negócio:
                    </p>
                  </div>
                </div>

                {/* Grid de planos — todos com mesmo peso visual; o cliente escolhe */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3" data-testid="trial-expired-plans-grid">
                  {[
                    { id: 'basico', name: 'Básico', price: '29,90', limit: '15 avaliações/mês', popular: false },
                    { id: 'pro', name: 'Pro', price: '39,90', limit: '30 avaliações/mês', popular: false },
                    { id: 'enterprise', name: 'Empresarial', price: '59,90', limit: '60 avaliações/mês', popular: true },
                    { id: 'elite', name: 'Elite', price: '99,90', limit: '100 avaliações/mês', popular: false },
                    { id: 'five_stars', name: '5 Estrelas', price: '149,90', limit: 'Avaliações ilimitadas', popular: false },
                  ].map((p) => (
                    <Link
                      key={p.id}
                      to={`/plans?plan=${p.id}`}
                      data-testid={`trial-expired-plan-${p.id}`}
                      className={`relative rounded-xl border-2 p-3 flex flex-col gap-2 transition-all hover:shadow-md ${
                        p.popular
                          ? (dm ? 'border-[#C5A059] bg-[#C5A059]/10' : 'border-[#C5A059] bg-[#FFF8E7]')
                          : (dm ? 'border-slate-700 bg-slate-900/40 hover:border-slate-500' : 'border-slate-200 bg-white hover:border-slate-300')
                      }`}
                    >
                      {p.popular && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#C5A059] text-[#0A2647] whitespace-nowrap">
                          ⭐ Mais Popular
                        </span>
                      )}
                      <p className={`text-sm font-bold ${dm ? 'text-slate-100' : 'text-[#0A2647]'}`}>{p.name}</p>
                      <p className={`text-lg font-bold ${dm ? 'text-emerald-400' : 'text-emerald-700'}`}>
                        R$ {p.price}<span className="text-xs font-normal text-slate-500">/mês</span>
                      </p>
                      <p className={`text-xs ${dm ? 'text-slate-400' : 'text-slate-600'}`}>{p.limit}</p>
                      <Button
                        size="sm"
                        className={`w-full text-xs h-8 mt-1 ${p.popular ? 'bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647]' : 'bg-[#0A2647] hover:bg-[#143057] text-white'} font-semibold`}
                      >
                        Continuar com {p.name}
                      </Button>
                    </Link>
                  ))}
                </div>

                <p className={`text-xs text-center mt-2 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
                  <Link to="/plans" className="underline hover:text-[#C5A059]" data-testid="trial-expired-compare-link">
                    Ver comparação detalhada de todos os recursos →
                  </Link>
                </p>
              </div>
            ) : planUsage.free_trial_percent >= 80 ? (
              <div className={`rounded-xl p-5 mb-6 border ${dm ? 'bg-amber-950/40 border-amber-800/50' : 'bg-amber-50 border-amber-200'}`} data-testid="free-trial-warning-alert">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Gauge className={`w-5 h-5 ${dm ? 'text-amber-400' : 'text-amber-600'}`} />
                      <p className={`font-semibold ${dm ? 'text-amber-400' : 'text-amber-700'}`}>
                        Seu período gratuito está acabando! {planUsage.free_trial_days_remaining} dias restantes
                      </p>
                    </div>
                    <p className={`text-sm mt-1 ${dm ? 'text-amber-300/80' : 'text-amber-600'}`}>
                      Veja todos os planos disponíveis e escolha o que melhor atende seu negócio.
                    </p>
                  </div>
                  <Link to="/plans">
                    <Button className="bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold rounded-xl whitespace-nowrap" data-testid="trial-warning-upgrade-btn">
                      Ver Planos
                    </Button>
                  </Link>
                </div>
              </div>
            ) : null
          )}

          {/* Plan Alerts */}
          {planUsage && !planUsage.is_unlimited && planUsage.usage_percentage >= 80 && (
            <div className={`rounded-xl p-5 mb-6 border ${planUsage.usage_percentage >= 100 ? (dm ? 'bg-red-950/40 border-red-800/50' : 'bg-red-50 border-red-200') : (dm ? 'bg-amber-950/40 border-amber-800/50' : 'bg-amber-50 border-amber-200')} flex flex-col sm:flex-row items-start sm:items-center gap-4`} data-testid={planUsage.usage_percentage >= 100 ? 'plan-limit-alert' : 'plan-warning-alert'}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {planUsage.usage_percentage >= 100 ? <AlertTriangle className={`w-5 h-5 ${dm ? 'text-red-400' : 'text-red-600'}`} /> : <Gauge className={`w-5 h-5 ${dm ? 'text-amber-400' : 'text-amber-600'}`} />}
                  <p className={`font-semibold ${planUsage.usage_percentage >= 100 ? (dm ? 'text-red-400' : 'text-red-700') : (dm ? 'text-amber-400' : 'text-amber-700')}`}>
                    {planUsage.usage_percentage >= 100 ? 'Limite mensal atingido!' : 'Você atingiu 80% do seu limite mensal!'}
                  </p>
                </div>
                <p className={`text-sm mt-1 ${planUsage.usage_percentage >= 100 ? (dm ? 'text-red-300/80' : 'text-red-600') : (dm ? 'text-amber-300/80' : 'text-amber-600')}`}>
                  {planUsage.usage_percentage >= 100 ? `Você atingiu ${planUsage.monthly_limit} avaliações este mes.` : `Faca upgrade para não perder nenhum feedback! (${planUsage.monthly_count}/${planUsage.monthly_limit} avaliações usadas)`}
                </p>
              </div>
              <Link to={planUsage.next_plan_id ? `/checkout/${planUsage.next_plan_id}` : '/plans'}>
                <Button className="bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] font-bold rounded-xl whitespace-nowrap" data-testid="upgrade-btn">
                  {planUsage.next_plan_name ? `${planUsage.usage_percentage >= 100 ? 'Assinar' : 'Upgrade:'} ${planUsage.next_plan_name}` : 'Ver Planos'}
                </Button>
              </Link>
            </div>
          )}

          <FirstStepsChecklist companyId={user?.company_id} totalFeedbacks={stats?.total_feedbacks || 0} theme={{ dm }} />

          <DashboardKPIs
            stats={stats}
            theme={theme}
            monthlyEvolution={monthlyEvolution}
            loadMonthlyEvolution={loadMonthlyEvolution}
            openHistory={openHistory}
          />
          <DashboardComparison stats={stats} planUsage={planUsage} theme={theme} navigate={navigate} />
          <DashboardReferral referralData={referralData} loadingReferral={loadingReferral} referralLink={referralLink} copyReferralLink={copyReferralLink} shareReferralWhatsApp={shareReferralWhatsApp} theme={theme} companyId={user?.company_id} />

          {/* Export Buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Button onClick={exportPDF} className="btn-primary gap-2" data-testid="export-pdf-btn"><FileText className="w-4 h-4" />Exportar PDF</Button>
            <Button onClick={exportExcel} variant="outline" className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-excel-btn"><Table2 className="w-4 h-4" />Exportar Excel</Button>
            <span className="flex items-center text-xs text-slate-400">{feedbacks.length} avaliação(oes) no período selecionado</span>
          </div>

          {/* Charts + Feed */}
          <div className="grid lg:grid-cols-12 gap-6">
            <DashboardCharts chartData={chartData} chartColors={chartColors} complaintTagsStats={complaintTagsStats} theme={theme} getToken={getToken} />
            <DashboardFeedbackFeed feedbacks={feedbacks} hiddenCount={hiddenCount} showArchived={showArchived} getFeedbackTypeName={getFeedbackTypeName} openWhatsApp={openWhatsApp} openEmail={openEmail} archiveFeedback={archiveFeedback} planUsage={planUsage} setShowWhatsAppLock={setShowWhatsAppLock} setShowArchiveLock={setShowArchiveLock} setViewingPhoto={setViewingPhoto} theme={theme} canArchive={canArchive} />
          </div>
        </main>

        <DashboardModals showWhatsAppLock={showWhatsAppLock} setShowWhatsAppLock={setShowWhatsAppLock} viewingPhoto={viewingPhoto} setViewingPhoto={setViewingPhoto} showArchiveLock={showArchiveLock} setShowArchiveLock={setShowArchiveLock} />
        <KPIHistoryModal
          open={!!historyMetric}
          onClose={() => setHistoryMetric(null)}
          metric={historyMetric}
          monthlyEvolution={monthlyEvolution}
          loading={loadingEvolution && monthlyEvolution.length === 0}
          theme={theme}
          companyName={user?.company_name}
        />
        <HelpButton ambiente="dono" tela="dashboard" />
      </div>
    </div>
  );
};

export default Dashboard;
